import { useParams, useLocation } from "wouter";
import { Link } from "wouter";
import {
  useGetQuote,
  useGetQuoteSimilar,
  getGetQuoteQueryKey,
  getGetQuoteSimilarQueryKey,
} from "@workspace/api-client-react";
import { useAuth } from "@/contexts/AuthContext";
import { QuoteCard } from "@/components/QuoteCard";
import { TagChip } from "@/components/TagChip";
import { SharePanel } from "@/components/SharePanel";
import { usePageMeta } from "@/hooks/usePageMeta";

function toCardQuote(q: {
  id: number;
  text: string;
  author: string;
  era: string;
  year?: number | null;
  tags: { name: string }[];
}) {
  return {
    id: q.id,
    text: q.text,
    author: q.author,
    era: q.era,
    year: q.year != null ? String(q.year) : q.era,
    tags: q.tags.map((t) => t.name),
  };
}

export function QuotePage() {
  const params = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { currentUser } = useAuth();

  const quoteId = Number(params.id);

  const similarParams = { limit: 4 };
  const { data: quote, isLoading, isError } = useGetQuote(quoteId, {
    query: { queryKey: getGetQuoteQueryKey(quoteId), enabled: !isNaN(quoteId) },
  });

  const { data: similar } = useGetQuoteSimilar(quoteId, similarParams, {
    query: { queryKey: getGetQuoteSimilarQueryKey(quoteId, similarParams), enabled: !isNaN(quoteId) },
  });

  const base = import.meta.env.BASE_URL.replace(/\/$/, "");

  const quoteSnippet = quote
    ? `\u201C${quote.text.length > 100 ? `${quote.text.slice(0, 99)}\u2026` : quote.text}\u201D`
    : null;
  usePageMeta({
    title: quote
      ? `${quoteSnippet} \u2014 ${quote.author} \u00B7 QuotedsArrow`
      : "QuotedsArrow — Literary Quote Discovery",
    description: quote
      ? `${quote.text.slice(0, 200)}${quote.text.length > 200 ? "\u2026" : ""} \u2014 ${quote.author}`
      : undefined,
  });

  return (
    <div className="min-h-screen bg-stone-100 font-sans text-stone-900">
      {/* Minimal top bar */}
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4 md:px-6">
          <button
            onClick={() => navigate(currentUser ? "/" : "/")}
            className="flex items-center gap-2 rounded-xl px-2 py-1.5 text-sm font-medium text-stone-500 transition hover:bg-stone-100 hover:text-stone-900 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1"
          >
            ← {currentUser ? "Back to app" : "QuotedsArrow"}
          </button>
          <div className="flex items-center gap-2">
            {quote && (
              <SharePanel
                quoteId={quoteId}
                text={quote.text}
                author={quote.author}
                triggerClassName="rounded-full border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-500 transition hover:border-stone-300 hover:bg-stone-50 hover:text-stone-700 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1"
                triggerLabel="↗ Share"
                data-testid="btn-share-quote-page"
              />
            )}
            {!currentUser && (
              <Link
                href="/"
                className="rounded-2xl bg-stone-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-stone-700 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1"
              >
                Sign in to save quotes
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 md:px-6">
        {isLoading ? (
          <div className="rounded-3xl border border-stone-200 bg-white p-16 text-center shadow-sm">
            <p className="animate-pulse text-sm text-stone-400">Loading quote…</p>
          </div>
        ) : isError || !quote ? (
          <div className="rounded-3xl border border-stone-200 bg-white p-16 text-center shadow-sm">
            <p className="mb-2 text-lg font-semibold text-stone-900">Quote not found</p>
            <p className="mb-6 text-sm text-stone-500">This quote may have been removed.</p>
            <Link
              href={`${base}/`}
              className="rounded-2xl bg-stone-900 px-6 py-2.5 text-sm font-medium text-white transition hover:bg-stone-700 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1"
            >
              Go home
            </Link>
          </div>
        ) : (
          <>
            {/* Main quote */}
            <div className="mb-8 rounded-3xl border border-stone-200 bg-white p-8 shadow-sm">
              <p className="mb-6 font-serif text-3xl leading-relaxed text-stone-900 md:text-4xl">
                &ldquo;{quote.text}&rdquo;
              </p>
              <div className="border-t border-stone-100 pt-5">
                <Link
                  href={`${base}/authors/${encodeURIComponent(quote.author)}`}
                  className="-mx-1 rounded-md px-1 text-lg font-semibold text-stone-900 transition-colors hover:bg-stone-100"
                >
                  {quote.author}
                </Link>
                <p className="mt-0.5 text-sm text-stone-500">
                  {quote.era}
                  {quote.year != null ? ` · ${quote.year}` : ""}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {quote.tags.map((t) => (
                    <Link key={t.name} href={`${base}/tags/${encodeURIComponent(t.name)}`}>
                      <TagChip>{t.name}</TagChip>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* CTA for signed-out visitors */}
            {!currentUser && (
              <div className="mb-8 rounded-3xl border border-stone-200 bg-white p-6 text-center shadow-sm">
                <p className="mb-1 font-semibold text-stone-900">Discover more quotes like this</p>
                <p className="mb-4 text-sm text-stone-500">
                  Sign in to save quotes and let the matching engine learn your taste.
                </p>
                <Link
                  href="/"
                  className="inline-block rounded-2xl bg-stone-900 px-6 py-2.5 text-sm font-medium text-white transition hover:bg-stone-700 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1"
                >
                  Get started →
                </Link>
              </div>
            )}

            {/* Similar quotes */}
            {similar && similar.length > 0 && (
              <div>
                <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-stone-500">
                  Similar quotes
                </h2>
                <div className="grid gap-5 sm:grid-cols-2">
                  {similar.map((q) => (
                    <QuoteCard key={q.id} quote={toCardQuote(q)} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
